import { useElectronPrintersActions } from '@store/actions/electronPrinters';
import { useEffect } from 'react';
import { useElectron } from './useElectron';
import toast from 'react-hot-toast';
import { ElectronPrinter } from '@interfaces/general';
import { capturePrintError } from '@utils/print/printSentry';

interface RefreshPrintersOptions {
  // En el arranque silenciamos el toast: el caso típico es un shell Electron viejo sin el
  // handler `getPrinters` (esperado, no accionable por el usuario). El refresh manual sí avisa.
  silentErrors?: boolean;
}

// Refresca la lista de impresoras detectadas (IPC getPrinters) SIN efecto de
// montaje. Úsalo para el botón "Actualizar lista" sin disparar un fetch
// redundante (la inicialización global vive en Layout vía useInitElectronPrinters).
export const useRefreshElectronPrinters = ({ silentErrors = false }: RefreshPrintersOptions = {}) => {
  const { initElectronPrinters } = useElectronPrintersActions();
  const { isElectronInstance, ipcRenderer } = useElectron();

  return async () => {
    if (!isElectronInstance) return;
    try {
      const printers: ElectronPrinter[] = await ipcRenderer.invoke('getPrinters');
      initElectronPrinters(printers);
    } catch (error) {
      // El shell Electron desactualizado no expone el IPC getPrinters → se distingue del
      // resto de fallos de IPC para poder correlacionar por versión/plataforma del shell.
      const isHandlerMissing = error?.toString().includes('No handler registered');
      capturePrintError(error, {
        tag: isHandlerMissing ? 'getPrinters_no_handler' : 'print_ipc_failure',
        trigger: silentErrors ? 'init' : 'manual',
      });
      // Un toast rojo en el arranque (mount) asustaba a usuarios de primer ingreso por un caso
      // benigno (shell viejo). El error sigue yendo a Sentry; el toast solo en acción del usuario.
      if (!silentErrors) {
        toast.error('Error al leer las impresoras instaladas');
      }
    }
  };
};

// Inicialización global (una sola vez, en Layout): detecta impresoras al montar.
export const useInitElectronPrinters = () => {
  const { isElectronInstance } = useElectron();
  const refreshPrinters = useRefreshElectronPrinters({ silentErrors: true });

  useEffect(() => {
    if (isElectronInstance) {
      refreshPrinters();
    }
  }, [isElectronInstance]);
};
